import { applyRichTextLink } from '@levitate/rich-text';
import { EditorContent } from '@tiptap/react';
import { css } from 'aphrodite';
import * as React from 'react';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { ISupportTicket } from '../../../models/Support/ISupportTicket';
import { Button } from '../../../web/components-v2/Button';
import { Input } from '../../../web/components-v2/Input';
import { ConfirmModal } from '../../components/supportShared/ConfirmModal';
import { ComposerAddressFields } from './TicketComposer/ComposerAddressFields';
import { ComposerAiPanel } from './TicketComposer/ComposerAiPanel';
import { ComposerAttachmentList } from './TicketComposer/ComposerAttachmentList';
import { ComposerBubbleMenu } from './TicketComposer/ComposerBubbleMenu';
import { ComposerErrorBanner } from './TicketComposer/ComposerErrorBanner';
import { ComposerFooter } from './TicketComposer/ComposerFooter';
import { ComposerFormattingBar } from './TicketComposer/ComposerFormattingBar';
import { ComposerHeader } from './TicketComposer/ComposerHeader';
import { ComposerLinkPopover } from './TicketComposer/ComposerLinkPopover';
import { ComposerSuggestionMenu } from './TicketComposer/ComposerSuggestionMenu';
import { composerStyles as styles } from './TicketComposer/composerStyles';
import type { ComposerMode } from './TicketComposer/types';
import { useComposerAi } from './TicketComposer/useComposerAi';
import { useComposerAttachments } from './TicketComposer/useComposerAttachments';
import {
	getComposerDraftStorageKey,
	useComposerDraft,
	usePersistComposerDraft,
} from './TicketComposer/useComposerDraft';
import { ComposerEditorCallbacks, getMentionedUserIds, useComposerEditor } from './TicketComposer/useComposerEditor';
import { useComposerInlineImages } from './TicketComposer/useComposerInlineImages';
import { useComposerSend } from './TicketComposer/useComposerSend';
import { useComposerSuggestions } from './TicketComposer/useComposerSuggestions';
import './styles/composerEditor.css';

export type { ComposerMode } from './TicketComposer/types';

interface TicketComposerProps {
	ticketId: string;
	ticket: ISupportTicket;
	mode: ComposerMode;
	onModeChange: (mode: ComposerMode) => void;
	replyType: 'reply' | 'note' | 'forward';
	onReplyTypeChange: (type: 'reply' | 'note' | 'forward') => void;
	/** Thread ID to forward (required when replyType is 'forward') */
	forwardThreadId?: string;
	/** Called after a successful "Send & close" — parent should close the ticket */
	onAfterSend?: () => void;
	/** Called when user confirms self-assign after replying to an unassigned ticket */
	onSelfAssign?: () => void;
	/** Opens the customer picker when the ticket has no reply recipient. */
	onChangeCustomer?: () => void;
	/** Called whenever the editor's "has content" state changes — parent uses this to pause polling. */
	onHasContentChange?: (hasContent: boolean) => void;
	/** When true, the mailbox has outbound sending gated off — show a read-only notice in reply/forward. */
	sendingDisabled?: boolean;
}

/** Imperative handle so the parent can inject content (e.g. accept AI draft) */
export interface TicketComposerHandle {
	setContent: (html: string) => void;
}

/**
 * TipTap-based ticket composer for support replies/notes: typographic tabs, hairline dividers,
 * formatting controls, a selection bubble, `/` saved replies, `@` mentions in notes, AI assist,
 * attachments and local drafts. This file owns
 * the wiring — editor callbacks and send plumbing — and delegates rendering to the
 * Composer* components and the self-contained subsystems to hooks (AI, send, draft, attachments).
 */
export const TicketComposer = React.forwardRef<TicketComposerHandle, TicketComposerProps>(
	(
		{
			ticketId,
			ticket,
			mode,
			onModeChange,
			replyType,
			onReplyTypeChange,
			forwardThreadId,
			onAfterSend,
			onSelfAssign,
			onChangeCustomer,
			onHasContentChange,
			sendingDisabled,
		},
		ref
	) => {
		const userSession = useAdminUserSession();
		const agentName = `${userSession.user.firstName} ${userSession.user.lastName}`;
		const draftStorageKey = React.useMemo(
			() => getComposerDraftStorageKey(userSession.user.id, ticketId),
			[ticketId, userSession.user.id]
		);
		const { initialDraft, clearDraft } = useComposerDraft(draftStorageKey);

		const replyTypeRef = React.useRef(replyType);
		replyTypeRef.current = replyType;

		const [expanded, setExpanded] = React.useState(false);
		const [hasContent, setHasContent] = React.useState(() => !!initialDraft?.html.trim());
		const [bodyHtml, setBodyHtml] = React.useState(initialDraft?.html ?? '');

		const [linkPopover, setLinkPopover] = React.useState<{ x: number; y: number } | null>(null);
		const linkPopoverElRef = React.useRef<HTMLDivElement>(null);
		const linkSelectionRef = React.useRef<{ from: number; to: number } | null>(null);

		const editorWrapRef = React.useRef<HTMLDivElement>(null);
		const [editorScrollTarget, setEditorScrollTarget] = React.useState<HTMLDivElement | null>(null);
		const suggestions = useComposerSuggestions({
			agentName,
			currentAccountId: userSession.account?.id,
			currentUserId: userSession.user.id,
			editorWrapRef,
			ticket,
		});

		const contactFirstName = (ticket.contactName ?? '').trim().split(/\s+/)[0] || '';

		const editorCallbacks: ComposerEditorCallbacks = {
			...suggestions.editorCallbacks,
			onUpdate: instance => {
				setHasContent(!instance.isEmpty);
				setBodyHtml(instance.isEmpty ? '' : instance.getHTML());
			},
			getPlaceholder: () => {
				switch (replyTypeRef.current) {
					case 'note':
						return 'Internal note — team only…';
					case 'forward':
						return 'Add an internal note about this forward…';
					default:
						return contactFirstName ? `Reply to ${contactFirstName}…` : 'Write a reply to the customer…';
				}
			},
			isNoteMode: () => replyTypeRef.current === 'note',
			// The body runs on paste/drop, long after render, so insertComposerFiles is initialized by then.
			insertFiles: (files, insertAt) => insertComposerFiles(files, insertAt),
		};
		const editor = useComposerEditor(editorCallbacks, initialDraft?.html ?? '');

		React.useImperativeHandle(ref, () => ({
			setContent: (html: string) => {
				editor?.commands.setContent(html);
				editor?.commands.focus('end');
			},
		}));

		const [ccValue, setCcValue] = React.useState(initialDraft?.ccValue ?? '');
		const [bccValue, setBccValue] = React.useState(initialDraft?.bccValue ?? '');
		const [forwardToValue, setForwardToValue] = React.useState(initialDraft?.forwardToValue ?? '');
		const [showCc, setShowCc] = React.useState(!!initialDraft?.ccValue);
		const [showBcc, setShowBcc] = React.useState(!!initialDraft?.bccValue);

		// The reply recipient follows the ticket's customer; changing the customer re-seeds it so the
		// agent always sees — and can override — exactly who the reply will go to.
		const [replyToValue, setReplyToValue] = React.useState(ticket.contactEmail ?? '');
		React.useEffect(() => {
			setReplyToValue(ticket.contactEmail ?? '');
		}, [ticket.contactEmail]);

		const ccSeeded = React.useRef(!!initialDraft?.ccValue);
		React.useEffect(() => {
			if (ticket?.ccEmails?.length && !ccSeeded.current) {
				setCcValue(ticket.ccEmails.join(', '));
				setShowCc(true);
				ccSeeded.current = true;
			}
		}, [ticket?.ccEmails]);

		const {
			addFiles,
			clearAttachments,
			dismissUploadError,
			fileInputRef,
			handleFileSelect,
			hasUploading: hasUploadingAttachments,
			pendingAttachments,
			removeAttachment,
			uploadError,
		} = useComposerAttachments();
		const inlineImages = useComposerInlineImages(editor);
		const hasUploading = hasUploadingAttachments || inlineImages.hasUploadingImages;

		const insertComposerFiles = (files: File[], insertAt?: number) => {
			const imageFiles = files.filter(file => file.type.startsWith('image/'));
			const otherFiles = files.filter(file => !file.type.startsWith('image/'));
			if (imageFiles.length > 0) inlineImages.insertImages(imageFiles, insertAt);
			if (otherFiles.length > 0) addFiles(otherFiles);
		};

		const imageInputRef = React.useRef<HTMLInputElement>(null);
		const handleImageFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
			const files = e.target.files;
			if (!files || files.length === 0) return;
			insertComposerFiles(Array.from(files));
			e.target.value = '';
		};

		// Composer-wide drop target: the editor's own handleDrop covers position-aware drops on the
		// text, this covers everywhere else on the card. Depth counting survives child enter/leave.
		const [isDropTarget, setIsDropTarget] = React.useState(false);
		const dragDepthRef = React.useRef(0);
		const dragHasFiles = (event: React.DragEvent) => event.dataTransfer.types.includes('Files');
		const handleComposerDragEnter = (event: React.DragEvent) => {
			if (!dragHasFiles(event)) return;
			event.preventDefault();
			dragDepthRef.current += 1;
			setIsDropTarget(true);
		};
		const handleComposerDragOver = (event: React.DragEvent) => {
			if (dragHasFiles(event)) event.preventDefault();
		};
		const handleComposerDragLeave = (event: React.DragEvent) => {
			if (!dragHasFiles(event)) return;
			dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
			if (dragDepthRef.current === 0) setIsDropTarget(false);
		};
		const handleComposerDrop = (event: React.DragEvent) => {
			dragDepthRef.current = 0;
			setIsDropTarget(false);
			// defaultPrevented marks drops the editor already handled at their exact position.
			if (event.defaultPrevented) return;
			const files = Array.from(event.dataTransfer.files);
			if (files.length === 0) return;
			event.preventDefault();
			insertComposerFiles(files);
		};

		const ai = useComposerAi({ ticketId, editor });

		const suggestionMenuOpen = suggestions.isOpen;

		const linkPopoverOpen = linkPopover != null;
		React.useEffect(() => {
			if (!linkPopoverOpen) return;
			const handleMouseDown = (event: MouseEvent) => {
				if (linkPopoverElRef.current?.contains(event.target as Node)) return;
				setLinkPopover(null);
			};
			document.addEventListener('mousedown', handleMouseDown);
			return () => document.removeEventListener('mousedown', handleMouseDown);
		}, [linkPopoverOpen]);

		const openLinkPopover = (anchor?: { x: number; y: number }) => {
			if (!editor) return;
			const { from, to } = editor.state.selection;
			linkSelectionRef.current = { from, to };
			const wrapWidth = editorWrapRef.current?.getBoundingClientRect().width ?? 600;
			setLinkPopover(
				anchor ? { x: Math.max(8, Math.min(anchor.x - 140, wrapWidth - 290)), y: anchor.y } : { x: 16, y: 8 }
			);
		};

		const applyLink = (rawUrl: string) => {
			setLinkPopover(null);
			if (!editor) return;
			applyRichTextLink(editor, rawUrl, linkSelectionRef.current ?? editor.state.selection);
		};
		const bubbleSuppressed = suggestionMenuOpen || linkPopoverOpen;

		const clearEditor = () => {
			clearDraft();
			editor?.commands.setContent('');
			setHasContent(false);
			setBodyHtml('');
			suggestions.close();
			setLinkPopover(null);
			clearAttachments();
			inlineImages.dismissImageUploadError();
		};

		const hasCustomer = !!ticket.contactEmail?.trim();
		const send = useComposerSend({
			bccValue,
			ccValue,
			clearEditor,
			forwardThreadId,
			forwardToValue,
			getEditorHtml: () => (editor && !editor.isEmpty ? editor.getHTML() : ''),
			getMentionedUserIds: () => getMentionedUserIds(editor),
			mode,
			onAfterSend,
			onModeChange,
			onReplyTypeChange,
			onSelfAssign,
			pendingAttachments,
			replyToValue,
			replyType,
			setBccValue,
			setCcValue,
			setForwardToValue,
			ticket,
			ticketId,
		});

		usePersistComposerDraft({
			bccValue,
			ccValue,
			forwardThreadId,
			forwardToValue,
			hasContent,
			html: bodyHtml,
			replyType,
			storageKey: draftStorageKey,
		});

		// Pause the parent's background polling while there is unsent content, so a refetch can't
		// clobber the in-progress reply.
		React.useEffect(() => {
			onHasContentChange?.(hasContent);
		}, [hasContent, onHasContentChange]);

		// The Placeholder decoration only re-renders on a transaction; nudge one when the type changes.
		React.useEffect(() => {
			editor?.view.dispatch(editor.state.tr);
		}, [editor, replyType]);

		React.useEffect(() => {
			if (mode === 'inline') editor?.commands.focus('end');
		}, [mode, editor]);

		// Collapsed hotkeys mirror the floating Reply / Note entry points.
		React.useEffect(() => {
			if (mode !== 'collapsed') return;
			const handleKeyDown = (event: KeyboardEvent) => {
				if (event.metaKey || event.ctrlKey || event.altKey) return;
				const target = event.target as HTMLElement | null;
				if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
				const key = event.key.toLowerCase();
				if (key !== 'r' && key !== 'n') return;
				if (key === 'r' && !hasCustomer) return;
				event.preventDefault();
				onReplyTypeChange(key === 'r' ? 'reply' : 'note');
				onModeChange('inline');
			};
			document.addEventListener('keydown', handleKeyDown);
			return () => document.removeEventListener('keydown', handleKeyDown);
		}, [mode, hasCustomer, onReplyTypeChange, onModeChange]);

		const errorBanners = [
			{ show: ai.aiError, message: 'AI assistance failed. Try again.', onDismiss: ai.dismissAiError },
			{ show: !!uploadError, message: uploadError ?? '', onDismiss: dismissUploadError },
			{
				show: !!inlineImages.imageUploadError,
				message: inlineImages.imageUploadError ?? '',
				onDismiss: inlineImages.dismissImageUploadError,
			},
			{ show: !!send.saveError, message: send.saveError ?? '', onDismiss: () => send.setSaveError(null) },
			{
				show: send.sendError,
				message: 'Reply saved but email delivery failed. Try resending.',
				onDismiss: () => send.setSendError(false),
			},
			{
				show: send.forwardError,
				message:
					'Note was saved but the forward failed. Retrying will send the forward without creating a duplicate note.',
				onDismiss: () => send.setForwardError(false),
			},
			{
				show: send.invalidRecipients,
				message:
					replyType === 'forward'
						? 'Enter at least one valid email address to forward.'
						: 'Enter a valid recipient email address.',
				onDismiss: () => send.setInvalidRecipients(false),
			},
		];
		const errorBannersNode = (
			<>
				{errorBanners
					.filter(banner => banner.show)
					.map(banner => (
						<ComposerErrorBanner key={banner.message} message={banner.message} onDismiss={banner.onDismiss} />
					))}
			</>
		);

		const selfAssignModal = send.showSelfAssign ? (
			<ConfirmModal
				title='Assign to Yourself?'
				message='This ticket is unassigned. Would you like to assign it to yourself?'
				confirmLabel='Assign to Me'
				cancelLabel='Keep Unassigned'
				onConfirm={() => {
					send.setShowSelfAssign(false);
					onSelfAssign?.();
				}}
				onCancel={() => send.setShowSelfAssign(false)}
			/>
		) : null;

		if (mode === 'collapsed') {
			return (
				<>
					<div className={css(styles.collapsedActions)} aria-label='Ticket composer actions'>
						{hasCustomer ? (
							<Button
								type='button'
								size='sm'
								title='Reply to customer (R)'
								onClick={() => {
									onReplyTypeChange('reply');
									onModeChange('inline');
								}}
							>
								Reply
							</Button>
						) : (
							<Button
								type='button'
								size='sm'
								onClick={onChangeCustomer}
								disabled={!onChangeCustomer}
								title={onChangeCustomer ? undefined : 'This ticket has no customer yet.'}
							>
								Add customer
							</Button>
						)}
						<Button
							type='button'
							variant='secondary'
							size='sm'
							title='Add internal note (N)'
							onClick={() => {
								onReplyTypeChange('note');
								onModeChange('inline');
							}}
						>
							Note
						</Button>
					</div>
					{errorBannersNode}
					{selfAssignModal}
				</>
			);
		}

		const draftLabel = send.isSending
			? ''
			: hasContent
				? 'Draft saved · sync paused'
				: hasUploading
					? 'Uploading…'
					: '';
		const showAiPanel = ai.pendingAction != null || ai.suggestion != null;

		return (
			<div className={css(styles.root)}>
				<div
					className={css(styles.card)}
					onDragEnter={handleComposerDragEnter}
					onDragOver={handleComposerDragOver}
					onDragLeave={handleComposerDragLeave}
					onDrop={handleComposerDrop}
				>
					{isDropTarget && (
						<div className={css(styles.dropOverlay)}>
							<span className={css(styles.dropOverlayLabel)}>Drop to add — images go inline, other files attach</span>
						</div>
					)}
					<ComposerHeader
						replyType={replyType}
						onReplyTypeChange={onReplyTypeChange}
						expanded={expanded}
						onToggleExpanded={() => setExpanded(!expanded)}
						onClose={() => onModeChange('collapsed')}
					/>
					<div className={css(styles.hairline)} />

					{sendingDisabled && replyType !== 'note' && (
						<div className={css(styles.noticeRow)} role='status'>
							<span className={css(styles.noticeDot, styles.noticeDotWarning)} />
							<span>
								Sending is disabled for this mailbox — this reply won't be delivered until sending is enabled.
							</span>
						</div>
					)}
					{!hasCustomer && replyType === 'reply' && (
						<div className={css(styles.noticeRow)} role='status'>
							<span className={css(styles.noticeDot, styles.noticeDotWarning)} />
							<span>This ticket has no customer yet.</span>
							{onChangeCustomer && (
								<Button type='button' variant='link' styles={[styles.noticeAction]} onClick={onChangeCustomer}>
									Add customer
								</Button>
							)}
						</div>
					)}

					<ComposerAddressFields
						replyType={replyType}
						ticket={ticket}
						replyToValue={replyToValue}
						setReplyToValue={setReplyToValue}
						forwardToValue={forwardToValue}
						setForwardToValue={setForwardToValue}
						ccValue={ccValue}
						setCcValue={setCcValue}
						bccValue={bccValue}
						setBccValue={setBccValue}
						showCc={showCc}
						setShowCc={setShowCc}
						showBcc={showBcc}
						setShowBcc={setShowBcc}
					/>

					<ComposerFormattingBar
						editor={editor}
						onOpenLink={() => openLinkPopover()}
						onInsertImage={() => imageInputRef.current?.click()}
						onAttachClick={() => fileInputRef.current?.click()}
					/>

					<div ref={editorWrapRef} className={css(styles.editorWrap)}>
						<div
							ref={setEditorScrollTarget}
							className={css(styles.editorScroll, expanded && styles.editorScrollExpanded)}
							onMouseDown={event => {
								// Clicking the empty space below the last line should still land the caret.
								if (event.target === event.currentTarget) {
									event.preventDefault();
									editor?.commands.focus('end');
								}
							}}
						>
							<EditorContent editor={editor} />
						</div>
						{editor && !bubbleSuppressed && (
							<ComposerBubbleMenu
								editor={editor}
								containerRef={editorWrapRef}
								scrollTarget={editorScrollTarget}
								showRefine={replyType === 'reply' && !ai.isBusy}
								onOpenLink={openLinkPopover}
								onRefineSelection={ai.runRefineSelection}
							/>
						)}
						{suggestions.menu && (
							<ComposerSuggestionMenu
								ref={suggestions.menuRef}
								menu={suggestions.menu}
								items={suggestions.items}
								onPick={suggestions.pick}
							/>
						)}
						{linkPopover && (
							<ComposerLinkPopover
								ref={linkPopoverElRef}
								position={linkPopover}
								onApply={applyLink}
								onDismiss={() => setLinkPopover(null)}
							/>
						)}
					</div>

					{showAiPanel && (
						<ComposerAiPanel
							label={ai.panelLabel}
							thinking={ai.pendingAction != null}
							suggestionHtml={ai.suggestion?.text ?? null}
							acceptLabel={ai.acceptLabel}
							onAccept={ai.acceptSuggestion}
							onDismiss={ai.dismissSuggestion}
						/>
					)}

					<ComposerAttachmentList attachments={pendingAttachments} onRemove={removeAttachment} />

					<ComposerFooter
						replyType={replyType}
						replyDisabled={!hasCustomer}
						isSending={send.isSending}
						hasUploading={hasUploading}
						hasContent={hasContent}
						forwardToValue={forwardToValue}
						showSendAndClose={!!onAfterSend}
						onSend={send.handleSend}
						onSendAndClose={send.handleSendAndClose}
						draftLabel={draftLabel}
						savedRepliesActive={suggestions.isSavedRepliesActive}
						savedRepliesBtnRef={suggestions.savedRepliesButtonRef}
						onToggleSavedReplies={suggestions.toggleSavedReplies}
						showAi={replyType === 'reply'}
						aiMenuOpen={ai.showAiMenu}
						aiMenuRef={ai.aiMenuRef}
						onToggleAiMenu={() => ai.setShowAiMenu(!ai.showAiMenu)}
						aiBusy={ai.isBusy}
						onRefine={ai.runRefine}
						onReview={ai.runReview}
						onDraft={ai.runDraft}
					/>
					<Input ref={fileInputRef} type='file' multiple styles={[styles.fileInput]} onChange={handleFileSelect} />
					<Input
						ref={imageInputRef}
						type='file'
						accept='image/*'
						multiple
						styles={[styles.fileInput]}
						onChange={handleImageFileSelect}
					/>
				</div>

				{errorBannersNode}
				{selfAssignModal}
			</div>
		);
	}
);

TicketComposer.displayName = 'TicketComposer';
